import * as ko from 'knockout';

import { app } from '../app';
import { Deferred } from '../utils/deferred';
import { DependentRecord, RemoveResult } from '../api/request';
import i18n from '../i18n';

let template = require('raw-loader!../../templates/components/remove_dialog.html').default;

// Fetched asynchronously after the remove dialog opens (the dialog shows a loading indicator
// until this resolves) so the user can still confirm in one step. blocking_records forbid
// deletion; when empty, deletion cascades — the entity is removed from cascade_records, shown
// under the caller-supplied cascade_message for confirmation.
export interface DependencyInfo {
  blocking_records: DependentRecord[];
  cascade_records: DependentRecord[];
  cascade_message?: string;
}

class RemoveDialog {
  name: string;
  msgs: string[];
  removeCount: number;
  private remove: () => Promise<RemoveResult>;
  private result: Deferred<{}>;

  deleting = ko.observable(false);
  // True while the dependency check is in flight; the dialog opens immediately and shows a
  // loading indicator instead of blocking the click until the dependencies resolve.
  loading = ko.observable(false);
  succeded = ko.observable(null);
  removeResult = ko.observable<RemoveResult>();
  // Set when deletion is allowed but cascades: lists what the entity will be removed from
  // (under a caller-supplied heading), switching the dialog into the confirm-removal state.
  cascadeRecords = ko.observable<DependentRecord[] | null>(null);
  cascadeMessage = ko.observable<string | null>(null);
  isBulkRemove = ko.pureComputed(() => this.removeCount >= 1);

  itemsText = ko.pureComputed(() => (this.removeCount > 1 ? i18n.t('items')() : i18n.t('item')()) + '.');
  confirmText = ko.pureComputed(() =>
    this.removeCount > 1
      ? i18n.t([
          'confirm_bulk_delete_many',
          'Are you sure you want to delete them?\nThis operation cannot be undone.',
        ])()
      : i18n.t([
          'confirm_bulk_delete_one',
          'Are you sure you want to delete it?\nThis operation cannot be undone.',
        ])()
  );

  constructor(params: {
    name: string;
    msgs: string[];
    removeCount: number;
    remove: () => Promise<RemoveResult>;
    result: Deferred<{}>;
    dependencies?: Promise<DependencyInfo>;
  }) {
    this.name = params.name;
    this.msgs = params.msgs;
    this.removeCount = params.removeCount;
    this.remove = params.remove;
    this.result = params.result;

    if (params.dependencies) {
      this.loading(true);
      params.dependencies
        .then((dependencies) => this.applyDependencies(dependencies))
        .catch((error) => console.warn('Failed to load dependent records for the remove dialog', error))
        .then(() => this.loading(false));
    }
  }

  private applyDependencies(dependencies: DependencyInfo) {
    const { blocking_records, cascade_records, cascade_message } = dependencies;
    if (blocking_records.length > 0) {
      // Cannot be removed: show every dependent record and disable the confirm button
      // (succeded set so the button's disabled binding matches the post-failed-attempt state).
      this.removeResult({
        did_delete: false,
        dependant_records: [...blocking_records, ...cascade_records],
      });
      this.succeded(false);
    } else if (cascade_records.length > 0) {
      this.cascadeRecords(cascade_records);
      this.cascadeMessage(cascade_message ?? null);
    }
  }

  onConfirm = () => {
    this.deleting(true);
    this.removeResult(null);

    this.remove()
      .then((data) => {
        this.deleting(false);
        if (data.did_delete) {
          this.succeded(true);
          this.result.resolve(null);
        } else {
          // Blocked — e.g. a trial was added since the dependency check; show why.
          this.succeded(false);
          this.removeResult(data);
        }
      })
      .catch(() => {
        this.deleting(false);
        this.succeded(false);
      });
  };

  onCancel = () => {
    this.result.reject();
  };
}

ko.components.register('remove-dialog', {
  viewModel: RemoveDialog,
  template: template,
});

export function removeDialog(
  name: string,
  msgs: string[],
  remove: () => Promise<RemoveResult>,
  removeCount: number = 0,
  dependencies?: Promise<DependencyInfo>
): Promise<{}> {
  return app.formsStackController.push({
    title: i18n.t('Confirm')(),
    name: 'remove-dialog',
    params: { name, msgs, removeCount, remove, result: new Deferred<{}>(), dependencies },
  });
}
